import React, { useState } from 'react';
import { Pause, Play, Loader2 } from 'lucide-react';

export interface LLMControlButtonProps {
  /** Current state: true = running, false = stopped */
  enabled: boolean;
  /** Whether the merchant's plan allows toggling */
  canToggle: boolean;
  /**
   * Called when the user wants to flip the switch.
   * Should call BE: POST /api/subscriptions/tenant/sdk-status { enabled }
   */
  onToggle: (nextEnabled: boolean) => Promise<void>;
}

/**
 * STOP / PLAY toggle for the LLM + website agent.
 *
 * Available for BOTH fixed and custom packages.
 *
 * Backed by the existing BE endpoint POST /api/subscriptions/tenant/sdk-status
 * which both halts LLM usage and hides the SDK widget on the merchant's site
 * (via the validateSdkAccess middleware).
 */
const LLMControlButton: React.FC<LLMControlButtonProps> = ({
  enabled,
  canToggle,
  onToggle,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (busy || !canToggle) return;
    setError(null);
    setBusy(true);
    try {
      await onToggle(!enabled);
    } catch (err: any) {
      setError(err?.message || 'Failed to update agent status');
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || !canToggle;
  const label = enabled ? 'Stop agent' : 'Resume agent';
  const Icon = busy ? Loader2 : enabled ? Pause : Play;

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={
          !canToggle
            ? 'Your plan does not allow toggling the agent'
            : enabled
              ? 'Stop the AI agent and hide the website widget'
              : 'Resume the AI agent and show the website widget'
        }
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors border ${
          disabled
            ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600 text-gray-500'
            : enabled
              ? 'border-red-500/50 text-red-600 dark:text-red-300 hover:bg-red-500/10'
              : 'border-green-500/50 text-green-600 dark:text-green-300 hover:bg-green-500/10'
        }`}
      >
        <Icon
          className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`}
          aria-hidden
        />
        <span>{busy ? 'Saving…' : label}</span>
      </button>

      {!enabled && !busy && (
        <span className="text-[11px] text-red-500">
          Agent is currently stopped
        </span>
      )}
      {error && (
        <span className="text-[11px] text-red-500" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default LLMControlButton;
